/**
 * Phase 2G.6 - payment tracking & cash accountability vocabulary.
 *
 * Payments in this system are recorded ONCE, at receiving
 * (LaundryTransaction.paymentMethod + amountPaidCents, integer cents,
 * server-validated against the total). There is no payment ledger table
 * and no later payment events, so:
 *
 *   - a "payment record" is the receiving payment (actor = receiver,
 *     timestamp = receiving time),
 *   - a release row only SNAPSHOTS payment state (it is never a payment),
 *   - collections are dated by the moment the payment was recorded -
 *     the only payment timestamp the model has - and this limitation is
 *     disclosed rather than fabricated around.
 *
 * The flags below are FACTUAL predicates over stored fields: no
 * accusation, no severity score, no deadline, no fraud label. Missing or
 * inconsistent records are reported as recorded, nothing more.
 */
import { type PaymentStatus } from './money.js';

/** Every status derivePaymentStatus() can produce (shared with money.ts). */
export const PAYMENT_STATUSES = [
  'UNPAID',
  'PARTIAL',
  'PAID',
  'OVERPAID',
] as const satisfies readonly PaymentStatus[];

export interface DashboardFinancials {
  netIncomeCents: number;
  salesCents: number;
  revenueCents: number;
  expensesCents: number;
  purchasesCents: number;
  dailySeries: Array<{ date: string; sales: number; expenses: number }>;
}

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  UNPAID: 'Unpaid',
  PARTIAL: 'Partially paid',
  PAID: 'Fully paid',
  OVERPAID: 'Overpaid / inconsistent',
};

/**
 * Factual payment findings a report can derive from stored data alone.
 *
 *   OUTSTANDING            - recorded payment is below the total
 *   NO_PAYMENT             - no payment is recorded at all
 *   PARTIAL_PAYMENT        - some but not all of the total is recorded
 *   OVERPAID               - recorded payment exceeds the total (only
 *                            possible on inconsistent stored data)
 *   METHOD_NOT_RECORDED    - a payment exists without a method
 *   ATTRIBUTION_MISSING    - the receiving record carries no actor
 *   RELEASE_SNAPSHOT_MISMATCH - the immutable release snapshot disagrees
 *                            with the transaction's recorded payment
 */
export const PAYMENT_FLAGS = [
  'OUTSTANDING',
  'NO_PAYMENT',
  'PARTIAL_PAYMENT',
  'OVERPAID',
  'METHOD_NOT_RECORDED',
  'ATTRIBUTION_MISSING',
  'RELEASE_SNAPSHOT_MISMATCH',
] as const;
export type PaymentFlag = (typeof PAYMENT_FLAGS)[number];

export const PAYMENT_FLAG_LABELS: Record<PaymentFlag, string> = {
  OUTSTANDING: 'Outstanding balance',
  NO_PAYMENT: 'No payment recorded',
  PARTIAL_PAYMENT: 'Partial payment',
  OVERPAID: 'Payment exceeds total',
  METHOD_NOT_RECORDED: 'Method not recorded',
  ATTRIBUTION_MISSING: 'Recorder not recorded',
  RELEASE_SNAPSHOT_MISMATCH: 'Release snapshot mismatch',
};

/** One-line explanation shown next to each flag in the UI. */
export const PAYMENT_FLAG_HINTS: Record<PaymentFlag, string> = {
  OUTSTANDING: 'The recorded payment is lower than the transaction total',
  NO_PAYMENT: 'The transaction has no recorded payment',
  PARTIAL_PAYMENT: 'Part of the total was recorded as paid at receiving',
  OVERPAID: 'Recorded payment exceeds the transaction total (impossible through the API)',
  METHOD_NOT_RECORDED: 'A payment exists but no payment method was recorded',
  ATTRIBUTION_MISSING: 'The receiving record carries no actor snapshot',
  RELEASE_SNAPSHOT_MISMATCH: 'The release snapshot and the current record disagree',
};

/** True when the recorded payment is below the transaction total. */
export function hasOutstandingBalance(amountPaidCents: number, totalCents: number): boolean {
  return amountPaidCents < totalCents;
}
